import React, { useEffect, useState, useMemo } from "react";
import api from "../api/client";
import { Card, PageHeader, Button, Badge, Money } from "../components/ui";
import Modal, { FormField, FormRow, Input, Select, TextArea, SubmitButton, ErrorBanner } from "../components/Modal";
import DealIntelligenceModal from "../components/DealIntelligenceModal";
import CapitalSourcesPanel from "../components/CapitalSourcesPanel";

import DealSecuritiesPanel from "../components/DealSecuritiesPanel";
import DealTranchesPanel from "../components/DealTranchesPanel";
import t from "../components/theme";

const STRATEGY_SECTIONS = [
  { tag: "Convertible Note", title: "Convertible Notes", color: "#7c3aed" },
  { tag: "Convertible Preferred", title: "Convertible Preferreds", color: "#b45309" },
  { tag: "ELOC", title: "ELOC Facilities", color: "#0d9488" },
  { tag: "PPLN", title: "PPLNs (Profit Participating Loan Notes)", color: "#2563eb" },
  { tag: "deSPAC - Convertible Note", title: "deSPAC - Convertible Notes", color: "#dc2626" },
  { tag: "IPO", title: "IPOs", color: "#059669" },
  { tag: "ACE Deal", title: "ACE Deals", color: "#d97706" },
];
const KNOWN_TAGS = STRATEGY_SECTIONS.map(s => s.tag);

export default function Deals() {
  const [allDeals, setAllDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [statusFilter, setStatusFilter] = useState("Active");
  const [search, setSearch] = useState("");
  // What the last Close or Reopen did. Kept because the interesting part of
  // closing a deal is what it did *not* do — see `setDealStatus` below.
  const [statusResult, setStatusResult] = useState(null);
  const [statusBusy, setStatusBusy] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/securities/deals/").then((r) => {
      const all = r.data.results || r.data;
      setAllDeals(all);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(load, []);

  const activeCount = useMemo(() => allDeals.filter(d => (d.status || "Active") === "Active").length, [allDeals]);
  const closedCount = useMemo(() => allDeals.filter(d => d.status === "Closed").length, [allDeals]);

  // Filter by status + search
  const deals = useMemo(() => {
    let filtered = allDeals;
    if (statusFilter !== "All") {
      filtered = filtered.filter(d => (d.status || "Active") === statusFilter);
    }
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(d =>
        (d.deal_name || "").toLowerCase().includes(s) ||
        (d.counterparty || "").toLowerCase().includes(s) ||
        (d.security_ticker || "").toLowerCase().includes(s) ||
        (d.entity_name || "").toLowerCase().includes(s) ||
        (d.strategy_tag || "").toLowerCase().includes(s)
      );
    }
    return filtered;
  }, [allDeals, statusFilter, search]);

  const handleRowClick = (row) => {
    api.get(`/securities/deals/${row.id}/`).then((r) => setViewing(r.data));
  };

  // Close a deal, or reopen it, in one click.
  //
  // **It moves no position, and that is what the banner is for.** Every
  // valuation in the book reads the tax lot — NAV filters `is_open` and never
  // looks at a deal's status — so a deal marked Closed while its lots are open
  // goes on being an asset and showing on the dashboard. High Wire Networks,
  // Inc.'s 20% OID senior secured debenture and its Series F preferred are
  // exactly that. The server returns what is still open and the screen says so,
  // rather than letting the word "Closed" assert an empty deal (§0.25: a label
  // may not name a condition the code does not test).
  const setDealStatus = (deal, wanted) => {
    setStatusBusy(deal.id);
    setStatusResult(null);
    api.post(`/securities/deals/${deal.id}/set-status/`, { status: wanted })
      .then((r) => {
        setStatusResult(r.data);
        setAllDeals((rows) => rows.map((d) => (
          d.id === deal.id ? { ...d, status: r.data.status, open_lot_count: r.data.open_lot_count } : d
        )));
      })
      .catch((e) => setStatusResult({
        error: e.response?.data?.error || "Could not change the status.",
        deal_name: deal.deal_name,
      }))
      .finally(() => setStatusBusy(null));
  };

  // Columns for sortable tables within each strategy group
  const dealColumns = [
    { key: "deal_name", label: "Deal", render: (v) => <span style={{ fontWeight: 600, color: t.text }}>{v}</span> },
    { key: "counterparty", label: "Issuer", render: (v) => v || "—" },
    { key: "security_ticker", label: "Ticker", render: (v) => v ? <span style={{ fontFamily: t.fontMono, fontSize: 12, fontWeight: 500, color: t.accent }}>{v}</span> : "—" },
    { key: "entity_name", label: "Entity", render: (v) => <span style={{ fontSize: 12, color: t.textSecondary }}>{v || "—"}</span> },
    { key: "funding_date", label: "Funded", render: (v) => v ? <span style={{ fontFamily: t.fontMono, fontSize: 12 }}>{v}</span> : "—" },
    { key: "deal_size", label: "Size", align: "right", render: (v) => v ? <Money value={v} /> : "—" },
    { key: "status", label: "Status", render: (v, row) => (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <Badge text={v || "Active"} variant={v === "Active" ? "green" : v === "Closed" ? "gray" : "yellow"} dot />
        {/* A closed deal that still holds open lots. The status is a label on
            the deal; the position is on the lot, and the lot is what NAV and
            the dashboard read. Saying it here is the difference between
            "why is HWNI showing?" and a fact anybody can see. */}
        {v === "Closed" && row.open_lot_count > 0 && (
          <span title="Closed, but still holding open positions — these are in NAV and on the dashboard until they are sold, redeemed or written off."
                style={{ fontSize: 11, color: t.warning || "#b45309", fontWeight: 600 }}>
            {row.open_lot_count} open
          </span>
        )}
      </span>
    )},
    { key: "id", label: "", align: "right", render: (_v, row) => {
      const closed = row.status === "Closed";
      return (
        <button
          onClick={(e) => { e.stopPropagation(); setDealStatus(row, closed ? "Active" : "Closed"); }}
          disabled={statusBusy === row.id}
          title={closed ? "Reopen this deal" : "Close this deal — positions are unchanged"}
          style={{
            padding: "3px 10px", borderRadius: t.radiusFull, cursor: "pointer",
            border: `1px solid ${t.border}`, background: t.bgCard, color: t.textSecondary,
            fontFamily: t.fontBody, fontSize: 11, fontWeight: 600,
            opacity: statusBusy === row.id ? 0.5 : 1,
          }}
        >
          {statusBusy === row.id ? "…" : closed ? "Reopen" : "Close"}
        </button>
      );
    }},
  ];

  // Group deals by strategy
  const otherDeals = deals.filter(d => !KNOWN_TAGS.includes(d.strategy_tag || ""));

  const StatusTab = ({ label, value, count }) => {
    const active = statusFilter === value;
    return (
      <button
        onClick={() => setStatusFilter(value)}
        style={{
          padding: "6px 16px", borderRadius: t.radiusFull, fontFamily: t.fontBody,
          border: active ? `1.5px solid ${t.accent}` : `1px solid ${t.border}`,
          background: active ? t.accentLight : t.bgCard,
          color: active ? t.accent : t.textSecondary,
          fontSize: 12, fontWeight: active ? 600 : 500, cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 5,
        }}
      >
        {label}
        <span style={{
          background: active ? t.accent : t.border,
          color: active ? "#fff" : t.textMuted,
          borderRadius: t.radiusFull, padding: "1px 7px",
          fontSize: 10, fontWeight: 600, lineHeight: "14px",
        }}>{count}</span>
      </button>
    );
  };

  return (
    <div>
      <PageHeader
        title="Deals"
        subtitle={`${allDeals.length} active & closed deals`}
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setShowAI(true)} style={{
              padding: "8px 16px", borderRadius: 6, border: "none",
              background: "linear-gradient(135deg, #7c3aed, #2563eb)",
              color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
              boxShadow: "0 2px 8px rgba(99, 102, 241, 0.3)",
            }}>Parse Deal Docs</button>
            <Button onClick={() => setShowAdd(true)}>+ Add Deal</Button>
          </div>
        }
      />

      {/* What the last Close or Reopen actually did.
          The status moved; the positions did not. Anything still open is
          listed by name and quantity, because it is still in NAV and on the
          dashboard and the reader has to know that before they walk away. */}
      {statusResult && (
        <div role="status" style={{
          marginBottom: 14, padding: "10px 14px", borderRadius: t.radius,
          border: `1px solid ${statusResult.error ? (t.danger || "#dc2626") : t.border}`,
          background: t.bgCard, fontSize: 13, color: t.text, fontFamily: t.fontBody,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <div>
              {statusResult.error ? (
                <span style={{ color: t.danger || "#dc2626" }}>
                  {statusResult.deal_name}: {statusResult.error}
                </span>
              ) : (
                <>
                  <strong>{statusResult.deal_name}</strong> is now {statusResult.status}.{" "}
                  <span style={{ color: t.textSecondary }}>{statusResult.note}</span>
                  {statusResult.still_open?.length > 0 && (
                    <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: t.textSecondary }}>
                      {statusResult.still_open.map((lot) => (
                        <li key={lot.lot_id}>
                          {lot.security || lot.ticker || "—"} — {Number(lot.quantity).toLocaleString("en-US")}
                          {lot.currency ? ` ${lot.currency}` : ""}
                          {lot.trade_date ? `, ${lot.trade_date}` : ""}
                        </li>
                      ))}
                    </ul>
                  )}
                  {Number(statusResult.clearing_balance) !== 0 && statusResult.clearing_balance != null && (
                    <div style={{ marginTop: 6, color: t.textSecondary }}>
                      Cash clearing on this deal is {Number(statusResult.clearing_balance).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}, not nil — money not yet matched to a bank row.
                    </div>
                  )}
                </>
              )}
            </div>
            <button onClick={() => setStatusResult(null)} aria-label="Dismiss" style={{
              border: 0, background: "none", cursor: "pointer", color: t.textMuted,
              fontSize: 16, lineHeight: 1, padding: 0,
            }}>×</button>
          </div>
        </div>
      )}

      {/* Status toggles + search */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", gap: 6 }}>
          <StatusTab label="Active" value="Active" count={activeCount} />
          <StatusTab label="Closed" value="Closed" count={closedCount} />
          <StatusTab label="All" value="All" count={allDeals.length} />
        </div>
        <input
          type="text"
          placeholder="Search deals, issuers, tickers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: "7px 12px", borderRadius: t.radius, border: `1px solid ${t.border}`,
            background: t.bgInput, color: t.text, fontSize: 13, fontFamily: t.fontBody,
            width: 280, outline: "none", boxSizing: "border-box",
          }}
        />
      </div>

      {/* Strategy-grouped sections with sortable tables */}
      {STRATEGY_SECTIONS.map(({ tag, title, color }) => {
        const sectionDeals = deals.filter(d => (d.strategy_tag || "") === tag);
        if (sectionDeals.length === 0) return null;
        return (
          <div key={tag} style={{ marginBottom: 24 }}>
            <SectionHeader title={title} count={sectionDeals.length} color={color} />
            <Card noPad>
              <SortableDealsTable columns={dealColumns} rows={sectionDeals} onRowClick={handleRowClick} />
            </Card>
          </div>
        );
      })}
      {otherDeals.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <SectionHeader title="Other" count={otherDeals.length} color="#345479" />
          <Card noPad>
            <SortableDealsTable columns={dealColumns} rows={otherDeals} onRowClick={handleRowClick} />
          </Card>
        </div>
      )}

      {!loading && deals.length === 0 && (
        <Card>
          <div style={{ padding: 40, textAlign: "center", color: t.textMuted, fontSize: 13 }}>
            No deals match your filters
          </div>
        </Card>
      )}

      {showAdd && <DealFormModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {viewing && <DealDetailModal deal={viewing} onClose={() => setViewing(null)} onSaved={() => { setViewing(null); load(); }} />}
      {showAI && <DealIntelligenceModal onClose={() => setShowAI(false)} onSaved={() => { setShowAI(false); load(); }} />}
    </div>
  );
}

function SectionHeader({ title, count, color }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, paddingLeft: 2 }}>
      <span style={{ fontSize: 15, fontWeight: 700, color: t.text, fontFamily: t.fontDisplay }}>{title}</span>
      <span style={{ fontSize: 11, fontWeight: 600, color: "#fff", background: color || "#345479", borderRadius: 12, padding: "2px 10px" }}>{count}</span>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// SORTABLE DEALS TABLE — themed to Warm Stone design system
// ═══════════════════════════════════════════════════════════════

function SortableDealsTable({ columns, rows, loading, onRowClick }) {
  const [sortKey, setSortKey] = useState("funding_date");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      let aVal = a[sortKey], bVal = b[sortKey];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const numA = Number(aVal), numB = Number(bVal);
      if (!isNaN(numA) && !isNaN(numB)) return sortDir === "asc" ? numA - numB : numB - numA;
      if (typeof aVal === "string" && /^\d{4}-\d{2}-\d{2}/.test(aVal)) {
        const dA = new Date(aVal).getTime(), dB = new Date(bVal).getTime();
        if (!isNaN(dA) && !isNaN(dB)) return sortDir === "asc" ? dA - dB : dB - dA;
      }
      aVal = String(aVal).toLowerCase(); bVal = String(bVal).toLowerCase();
      return sortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [rows, sortKey, sortDir]);

  if (loading) return <div style={{ padding: 48, textAlign: "center", color: t.textMuted, fontFamily: t.fontBody }}>Loading...</div>;
  if (rows.length === 0) return <div style={{ padding: 48, textAlign: "center", color: t.textMuted, fontSize: 13, fontFamily: t.fontBody }}>No deals match your filters</div>;

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: t.fontBody }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                onClick={() => handleSort(col.key)}
                style={{
                  textAlign: col.align || "left",
                  padding: "11px 16px",
                  borderBottom: `2px solid ${t.border}`,
                  background: t.tableHeaderBg,
                  color: sortKey === col.key ? t.accent : t.textMuted,
                  fontWeight: 600, fontSize: 11, textTransform: "uppercase",
                  letterSpacing: "0.04em", whiteSpace: "nowrap",
                  cursor: "pointer", userSelect: "none",
                  fontFamily: t.fontBody,
                }}
              >
                {col.label}
                <span style={{ marginLeft: 4, fontSize: 9, opacity: sortKey === col.key ? 1 : 0.3 }}>
                  {sortKey === col.key ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr
              key={row.id || i}
              onClick={() => onRowClick && onRowClick(row)}
              style={{
                cursor: onRowClick ? "pointer" : "default",
                borderBottom: `1px solid ${t.borderLight}`,
                background: i % 2 === 1 ? t.tableStripeBg : "transparent",
                transition: t.transitionFast,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = t.tableRowHover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 1 ? t.tableStripeBg : "transparent")}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  style={{
                    padding: "11px 16px",
                    textAlign: col.align || "left",
                    color: t.text,
                    fontSize: 13,
                    whiteSpace: "nowrap",
                    fontFamily: t.fontBody,
                  }}
                >
                  {col.render ? col.render(row[col.key], row) : (row[col.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// ADD DEAL — One-stop form. Security terms + deal economics.
// ═══════════════════════════════════════════════════════════════

function DealFormModal({ onClose, onSaved }) {
  const [securities, setSecurities] = useState([]);
  const [entities, setEntities] = useState([]);
  const [selectedSec, setSelectedSec] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    // Deal core
    deal_name: "", counterparty: "", security: "", entity: "",
    strategy_tag: "", status: "Active", funding_date: "",
    deal_size: "", notes: "",
    // Deal economics (Note/Convertible)
    face_value: "", net_amount: "", oid_amount: "", oid_pct: "",
    warrant_coverage_pct: "", coupon_frequency: "",
    // Note terms
    principal_amount: "", coupon_rate: "", day_count: "", interest_mode: "",
    maturity_date: "", issue_date: "", conversion_price: "",
    conversion_discount_pct: "", conversion_vwap_lookback: "",
    conversion_floor_price: "", conversion_ceiling_price: "",
    // Warrant terms
    strike_price: "", warrant_shares: "", warrant_expiry: "",
    // ELOC terms
    eloc_commitment_amount: "", eloc_discount_pct: "",
    eloc_discount_pct_2: "", eloc_discount_pct_3: "",
    eloc_pricing_days: "", eloc_settlement_days: "",
    eloc_max_advance: "", eloc_ownership_limit_pct: "",
    eloc_exchange_cap_pct: "", eloc_commitment_period_months: "",
    eloc_commitment_fee_shares: "", eloc_commitment_fee_amount: "",
    eloc_pricing_method: "", eloc_exchange: "",
  });

  useEffect(() => {
    api.get("/securities/securities/").then((r) => setSecurities(r.data.results || r.data));
    api.get("/core/entities/").then((r) => setEntities(r.data.results || r.data));
  }, []);

  const set = (f) => (v) => setForm((prev) => ({ ...prev, [f]: v }));

  const handleSecurityChange = (secId) => {
    const sec = securities.find((s) => String(s.id) === String(secId));
    setSelectedSec(sec || null);
    if (!sec) { setForm((prev) => ({ ...prev, security: secId })); return; }

    // Auto-detect strategy from instrument type
    const autoStrategy = {
      ELOC: "ELOC", Note: "Convertible", Warrant: "Warrant", Stock: "PIPE",
    }[sec.instrument_type] || "";

    setForm((prev) => ({
      ...prev,
      security: secId,
      counterparty: sec.issuer || prev.counterparty,
      strategy_tag: prev.strategy_tag || autoStrategy,
      deal_name: prev.deal_name || `${sec.issuer || sec.ticker} ${autoStrategy || "Deal"}`.trim(),
    }));

    // Fetch full security to pre-fill existing terms
    api.get(`/securities/securities/${secId}/`).then((r) => {
      const full = r.data;
      setSelectedSec(full);
      setForm((prev) => {
        const u = { ...prev };
        const fields = [
          "principal_amount", "coupon_rate", "day_count", "interest_mode",
          "maturity_date", "issue_date", "conversion_price",
          "conversion_discount_pct", "conversion_vwap_lookback",
          "conversion_floor_price", "conversion_ceiling_price",
          "strike_price", "warrant_shares", "warrant_expiry",
          "eloc_commitment_amount", "eloc_discount_pct",
          "eloc_discount_pct_2", "eloc_discount_pct_3",
          "eloc_pricing_days", "eloc_settlement_days",
          "eloc_max_advance", "eloc_ownership_limit_pct",
          "eloc_exchange_cap_pct", "eloc_commitment_period_months",
          "eloc_commitment_fee_shares", "eloc_commitment_fee_amount",
          "eloc_pricing_method", "eloc_exchange",
        ];
        fields.forEach((f) => { if (full[f] && !prev[f]) u[f] = String(full[f]); });
        return u;
      });
    });
  };

  // OID auto-calc
  const handleFaceValue = (v) => {
    const fv = parseFloat(v) || 0;
    const na = parseFloat(form.net_amount) || 0;
    setForm((prev) => ({
      ...prev, face_value: v, deal_size: v || prev.deal_size,
      oid_amount: na > 0 ? (fv - na).toFixed(4) : "",
      oid_pct: na > 0 && fv > 0 ? ((fv - na) / fv).toFixed(6) : "",
    }));
  };
  const handleNetAmount = (v) => {
    const na = parseFloat(v) || 0;
    const fv = parseFloat(form.face_value) || 0;
    setForm((prev) => ({
      ...prev, net_amount: v,
      oid_amount: fv > 0 ? (fv - na).toFixed(4) : "",
      oid_pct: fv > 0 ? ((fv - na) / fv).toFixed(6) : "",
    }));
  };

  const save = () => {
    if (!form.deal_name || !form.security || !form.entity) {
      return setError("Deal name, security, and entity are required");
    }
    setSaving(true); setError("");
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) payload[k] = v;
    });
    if (!payload.counterparty) payload.counterparty = selectedSec?.issuer || "TBD";

    api.post("/securities/deals/create-full/", payload)
      .then(() => onSaved())
      .catch((e) => {
        setSaving(false);
        setError(e.response?.data?.error || JSON.stringify(e.response?.data) || "Failed");
      });
  };

  // ── Dynamic type detection ──
  const instrType = (selectedSec?.instrument_type || "").toUpperCase();
  const isEloc = instrType === "ELOC" || form.strategy_tag === "ELOC";
  const isNote = instrType === "NOTE" || form.strategy_tag === "Convertible";
  const isWarrant = instrType === "WARRANT";
  const isPipe = instrType === "STOCK" || form.strategy_tag === "PIPE" || form.strategy_tag === "Direct";
  const showForm = !!form.security; // Only show detail sections after security is picked

  return (
    <Modal title="Add Deal" onClose={onClose} width={640}>
      <ErrorBanner message={error} />

      {/* ═══ STEP 1: Pick Security — this determines the rest ═══ */}
      <SectionLabel label="1. Select Security" />

      <FormField label="Security" required>
        <Select value={form.security} onChange={handleSecurityChange}
          options={securities.map((s) => ({
            value: s.id,
            label: `${s.ticker} — ${s.name} (${s.instrument_type})`,
          }))}
          placeholder="Select a security to begin..."
        />
      </FormField>

      {selectedSec && (
        <div style={{
          background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6,
          padding: 8, marginBottom: 12, fontSize: 11, color: "#0c4a6e",
          display: "flex", gap: 16, flexWrap: "wrap",
        }}>
          <span><strong>Type:</strong> {selectedSec.instrument_type}</span>
          <span><strong>Issuer:</strong> {selectedSec.issuer || "—"}</span>
          <span><strong>Ticker:</strong> {selectedSec.ticker}</span>
        </div>
      )}

      {!showForm && (
        <div style={{
          textAlign: "center", padding: 40, color: "#94a3b8", fontSize: 13,
        }}>
          Select a security above to configure the deal.
        </div>
      )}

      {showForm && (
        <>
          {/* ═══ STEP 2: Deal Info (always shown) ═══ */}
          <SectionLabel label="2. Deal Information" />

          <FormRow>
            <FormField label="Deal Name" required>
              <Input value={form.deal_name} onChange={set("deal_name")} placeholder="Auto-fills from issuer" />
            </FormField>
            <FormField label="Strategy" required>
              <Select value={form.strategy_tag} onChange={set("strategy_tag")} options={[
                { value: "Convertible Note", label: "Convertible Note" },
                { value: "Convertible Preferred", label: "Convertible Preferred" },
                { value: "ELOC", label: "ELOC" },
                { value: "ACE Deal", label: "ACE Deal" },
                { value: "Registered Direct", label: "Registered Direct (RD)" },
                { value: "PPLN", label: "PPLN" },
                { value: "IPO", label: "IPO" },
                { value: "deSPAC - Convertible Note", label: "deSPAC - Convertible Note" },
                { value: "Common Equity PIPE", label: "Common Equity PIPE" },
                { value: "Bridge Loan", label: "Bridge Loan" },
                { value: "Warrant", label: "Warrant" },
                { value: "Other", label: "Other" },
              ]} placeholder="Select strategy..." />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Entity" required>
              <Select value={form.entity} onChange={set("entity")}
                options={entities.map((e) => ({ value: e.id, label: e.name }))}
                placeholder="Select fund entity..."
              />
            </FormField>
            <FormField label="Funding Date">
              <Input type="date" value={form.funding_date} onChange={set("funding_date")} />
            </FormField>
          </FormRow>
          <FormField label="Total Deal Size">
            <Input type="number" value={form.deal_size} onChange={set("deal_size")}
              step="0.01" placeholder="Total investment amount" />
          </FormField>


          {/* ═══ ELOC-SPECIFIC FIELDS ═══ */}
          {isEloc && (
            <>
              <SectionLabel label="3. ELOC Facility Terms" color="#0369a1" />

              <FormRow>
                <FormField label="Facility Size (Commitment)">
                  <Input type="number" value={form.eloc_commitment_amount} onChange={set("eloc_commitment_amount")}
                    step="0.01" placeholder="10,000,000" />
                </FormField>
                <FormField label="Commitment Period (months)">
                  <Input type="number" value={form.eloc_commitment_period_months} onChange={set("eloc_commitment_period_months")}
                    placeholder="36" />
                </FormField>
              </FormRow>

              <SectionLabel label="Discount to VWAP" size="sm" />
              <FormRow>
                <FormField label="Tier 1 Discount %">
                  <Input type="pct" value={form.eloc_discount_pct} onChange={set("eloc_discount_pct")}
                    step="0.001" placeholder="0.05 = 5%" />
                  {form.eloc_discount_pct && (
                    <span style={{ fontSize: 10, color: "#059669" }}>
                      = {(Number(form.eloc_discount_pct) * 100).toFixed(2)}% discount to VWAP
                    </span>
                  )}
                </FormField>
                <FormField label="Tier 2 Discount % (optional)">
                  <Input type="pct" value={form.eloc_discount_pct_2} onChange={set("eloc_discount_pct_2")}
                    step="0.001" placeholder="0.08 = 8%" />
                  {form.eloc_discount_pct_2 && (
                    <span style={{ fontSize: 10, color: "#059669" }}>
                      = {(Number(form.eloc_discount_pct_2) * 100).toFixed(2)}%
                    </span>
                  )}
                </FormField>
              </FormRow>
              <FormField label="Tier 3 Discount % (optional)">
                <Input type="pct" value={form.eloc_discount_pct_3} onChange={set("eloc_discount_pct_3")}
                  step="0.001" placeholder="0.10 = 10%" />
                {form.eloc_discount_pct_3 && (
                  <span style={{ fontSize: 10, color: "#059669" }}>
                    = {(Number(form.eloc_discount_pct_3) * 100).toFixed(2)}%
                  </span>
                )}
              </FormField>

              <SectionLabel label="Pricing & Settlement" size="sm" />
              <FormRow>
                <FormField label="Pricing Period (days)">
                  <Input type="number" value={form.eloc_pricing_days} onChange={set("eloc_pricing_days")}
                    placeholder="2" />
                </FormField>
                <FormField label="Settlement Days (T+)">
                  <Input type="number" value={form.eloc_settlement_days} onChange={set("eloc_settlement_days")}
                    placeholder="2" />
                </FormField>
              </FormRow>
              <FormField label="Pricing Method">
                <TextArea value={form.eloc_pricing_method} onChange={set("eloc_pricing_method")}
                  placeholder="e.g., Lowest daily VWAP during the pricing period" rows={2} />
              </FormField>

              <SectionLabel label="Commitment Fee" size="sm" />
              <FormRow>
                <FormField label="Fee in Shares">
                  <Input type="number" value={form.eloc_commitment_fee_shares} onChange={set("eloc_commitment_fee_shares")}
                    placeholder="500,000" />
                </FormField>
                <FormField label="Fee in Dollars">
                  <Input type="number" value={form.eloc_commitment_fee_amount} onChange={set("eloc_commitment_fee_amount")}
                    step="0.01" placeholder="25,000" />
                </FormField>
              </FormRow>

              <SectionLabel label="Limits & Caps" size="sm" />
              <FormRow>
                <FormField label="Max Advance per Put">
                  <Input type="number" value={form.eloc_max_advance} onChange={set("eloc_max_advance")}
                    step="0.01" placeholder="1,000,000" />
                </FormField>
                <FormField label="Ownership Limit %">
                  <Input type="pct" value={form.eloc_ownership_limit_pct} onChange={set("eloc_ownership_limit_pct")}
                    step="0.001" placeholder="0.0999 = 9.99%" />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField label="Exchange Cap %">
                  <Input type="pct" value={form.eloc_exchange_cap_pct} onChange={set("eloc_exchange_cap_pct")}
                    step="0.001" placeholder="0.199 = 19.9%" />
                </FormField>
                <FormField label="Exchange">
                  <Select value={form.eloc_exchange} onChange={set("eloc_exchange")} options={[
                    { value: "", label: "— Select —" },
                    { value: "NYSE", label: "NYSE" },
                    { value: "NASDAQ", label: "NASDAQ" },
                    { value: "NYSE American", label: "NYSE American" },
                    { value: "OTC", label: "OTC Markets" },
                  ]} />
                </FormField>
              </FormRow>
            </>
          )}


          {/* ═══ NOTE / CONVERTIBLE-SPECIFIC FIELDS ═══ */}
          {isNote && (
            <>
              <SectionLabel label="3. Note / Convertible Terms" color="#7c3aed" />

              <FormRow>
                <FormField label="Face Value (Principal)">
                  <Input type="number" value={form.face_value} onChange={handleFaceValue}
                    step="0.01" placeholder="2,000,000" />
                </FormField>
                <FormField label="Net Amount (Cash Wired)">
                  <Input type="number" value={form.net_amount} onChange={handleNetAmount}
                    step="0.01" placeholder="1,800,000" />
                </FormField>
              </FormRow>
              {(form.oid_amount || form.oid_pct) && (
                <div style={{
                  background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6,
                  padding: 10, marginBottom: 12, fontSize: 12, color: "#92400e",
                  display: "flex", gap: 24,
                }}>
                  <span><strong>OID:</strong> ${Number(form.oid_amount || 0).toLocaleString()}</span>
                  <span><strong>OID %:</strong> {(Number(form.oid_pct || 0) * 100).toFixed(2)}%</span>
                </div>
              )}

              <FormRow>
                <FormField label="Coupon Rate">
                  <Input type="pct" value={form.coupon_rate} onChange={set("coupon_rate")}
                    step="0.001" placeholder="0.08 = 8%" />
                  {form.coupon_rate && (
                    <span style={{ fontSize: 10, color: "#64748b" }}>
                      = {(Number(form.coupon_rate) * 100).toFixed(2)}% annual
                    </span>
                  )}
                </FormField>
                <FormField label="Coupon Frequency">
                  <Select value={form.coupon_frequency} onChange={set("coupon_frequency")} options={[
                    { value: "", label: "— Select —" },
                    { value: "Monthly", label: "Monthly" },
                    { value: "Quarterly", label: "Quarterly" },
                    { value: "Semi-Annual", label: "Semi-Annual" },
                    { value: "Annual", label: "Annual" },
                    { value: "At Maturity", label: "At Maturity" },
                    { value: "PIK", label: "PIK (accrues)" },
                  ]} />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField label="Day Count">
                  <Select value={form.day_count} onChange={set("day_count")} options={[
                    { value: "", label: "— Select —" },
                    { value: "ACT/360", label: "Actual/360" },
                    { value: "ACT/365", label: "Actual/365" },
                    { value: "30/360", label: "30/360" },
                  ]} />
                </FormField>
                <FormField label="Interest Mode">
                  <Select value={form.interest_mode} onChange={set("interest_mode")} options={[
                    { value: "", label: "— Select —" },
                    { value: "Cash", label: "Cash Pay" },
                    { value: "PIK", label: "PIK" },
                    { value: "Both", label: "Cash + PIK" },
                  ]} />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField label="Issue Date">
                  <Input type="date" value={form.issue_date} onChange={set("issue_date")} />
                </FormField>
                <FormField label="Maturity Date">
                  <Input type="date" value={form.maturity_date} onChange={set("maturity_date")} />
                </FormField>
              </FormRow>
              <FormField label="Warrant Coverage %">
                <Input type="pct" value={form.warrant_coverage_pct} onChange={set("warrant_coverage_pct")}
                  step="0.01" placeholder="1.00 = 100%" />
              </FormField>

              <SectionLabel label="Conversion Terms" size="sm" />
              <div style={{
                background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 6,
                padding: 8, marginBottom: 10, fontSize: 11, color: "#5b21b6",
              }}>
                Deals often have both fixed and variable conversion mechanics.
                Fill in all applicable terms below.
              </div>
              <FormRow>
                <FormField label="Fixed Conversion Price">
                  <Input type="number" value={form.conversion_price} onChange={set("conversion_price")}
                    step="0.0001" placeholder="$2.50" />
                </FormField>
                <FormField label="Variable Discount %">
                  <Input type="pct" value={form.conversion_discount_pct} onChange={set("conversion_discount_pct")}
                    step="0.001" placeholder="0.10 = 10%" />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField label="VWAP Lookback (days)">
                  <Input type="number" value={form.conversion_vwap_lookback} onChange={set("conversion_vwap_lookback")}
                    placeholder="5" />
                </FormField>
                <FormField label="Floor Price">
                  <Input type="number" value={form.conversion_floor_price} onChange={set("conversion_floor_price")}
                    step="0.0001" placeholder="$0.50" />
                </FormField>
              </FormRow>
              <FormField label="Ceiling Price">
                <Input type="number" value={form.conversion_ceiling_price} onChange={set("conversion_ceiling_price")}
                  step="0.0001" placeholder="$10.00" />
              </FormField>
            </>
          )}


          {/* ═══ WARRANT-SPECIFIC FIELDS ═══ */}
          {isWarrant && (
            <>
              <SectionLabel label="3. Warrant Terms" color="#b45309" />
              <FormRow>
                <FormField label="Strike Price">
                  <Input type="number" value={form.strike_price} onChange={set("strike_price")}
                    step="0.0001" placeholder="$2.00" />
                </FormField>
                <FormField label="Number of Warrant Shares">
                  <Input type="number" value={form.warrant_shares} onChange={set("warrant_shares")}
                    placeholder="500,000" />
                </FormField>
              </FormRow>
              <FormField label="Expiration Date">
                <Input type="date" value={form.warrant_expiry} onChange={set("warrant_expiry")} />
              </FormField>
            </>
          )}


          {/* ═══ PIPE / DIRECT EQUITY ═══ */}
          {isPipe && !isEloc && !isNote && !isWarrant && (
            <>
              <SectionLabel label="3. Equity Terms" color="#0d9488" />
              <FormRow>
                <FormField label="Share Price">
                  <Input type="number" value={form.conversion_price} onChange={set("conversion_price")}
                    step="0.0001" placeholder="$3.50" />
                </FormField>
                <FormField label="Warrant Coverage %">
                  <Input type="pct" value={form.warrant_coverage_pct} onChange={set("warrant_coverage_pct")}
                    step="0.01" placeholder="1.00 = 100%" />
                </FormField>
              </FormRow>
            </>
          )}


          {/* ═══ Notes & Submit (always shown) ═══ */}
          <div style={{ marginTop: 12 }} />
          <FormField label="Notes">
            <TextArea value={form.notes} onChange={set("notes")} placeholder="Deal notes, special terms, etc." />
          </FormField>

          <SubmitButton onClick={save} loading={saving} label="Create Deal" />
        </>
      )}
    </Modal>
  );
}


// ═══════════════════════════════════════════════════════════════
// DEAL DETAIL / EDIT MODAL
// ═══════════════════════════════════════════════════════════════

function DealDetailModal({ deal: initialDeal, onClose, onSaved }) {
  const [writeOff, setWriteOff] = useState(null);
  const [shorts, setShorts] = useState(null);
  const num = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const qty = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 4 });
  const [deal, setDeal] = useState(initialDeal);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    deal_name: initialDeal.deal_name || "",
    counterparty: initialDeal.counterparty || "",
    security: initialDeal.security || "",
    entity: initialDeal.entity || "",
    strategy_tag: initialDeal.strategy_tag || "",
    status: initialDeal.status || "Active",
    deal_size: initialDeal.deal_size || "",
    funding_date: initialDeal.funding_date || "",
    face_value: initialDeal.face_value || "",
    net_amount: initialDeal.net_amount || "",
    oid_amount: initialDeal.oid_amount || "",
    oid_pct: initialDeal.oid_pct || "",
    warrant_coverage_pct: initialDeal.warrant_coverage_pct || "",
    coupon_frequency: initialDeal.coupon_frequency || "",
    vintage_year: initialDeal.vintage_year || "",
    affiliate_status: initialDeal.affiliate_status || "Non-Affiliate",
    beneficial_ownership_pct: initialDeal.beneficial_ownership_pct || "",
    incentive_fee_pct: initialDeal.incentive_fee_pct || "",
    management_fee_pct: initialDeal.management_fee_pct || "",
    preferred_return_pct: initialDeal.preferred_return_pct || "",
    notes: initialDeal.notes || "",
  });
  const [securities, setSecurities] = useState([]);
  const [entities, setEntities] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [warrants, setWarrants] = useState([]);
  const [showAddWarrant, setShowAddWarrant] = useState(false);
  const [newWarrant, setNewWarrant] = useState({ series_name: "", strike_price: "", num_shares: "", expiry_date: "", strike_price_method: "", expiry_method: "", is_prefunded: false, exercise_conditions: "", milestone_trigger: "", price_reset_enabled: false, price_reset_floor_pct: "", notes: "" });

  useEffect(() => {
    api.get(`/securities/deals/${initialDeal.id}/`).then((r) => {
      setDeal(r.data);
      setForm((prev) => ({
        ...prev,
        notes: r.data.notes || prev.notes,
        beneficial_ownership_pct: r.data.beneficial_ownership_pct || prev.beneficial_ownership_pct,
        incentive_fee_pct: r.data.incentive_fee_pct || prev.incentive_fee_pct,
        management_fee_pct: r.data.management_fee_pct || prev.management_fee_pct,
        preferred_return_pct: r.data.preferred_return_pct || prev.preferred_return_pct,
        funding_date: r.data.funding_date || prev.funding_date,
        face_value: r.data.face_value || prev.face_value,
        net_amount: r.data.net_amount || prev.net_amount,
        oid_amount: r.data.oid_amount || prev.oid_amount,
        oid_pct: r.data.oid_pct || prev.oid_pct,
        warrant_coverage_pct: r.data.warrant_coverage_pct || prev.warrant_coverage_pct,
        coupon_frequency: r.data.coupon_frequency || prev.coupon_frequency,
      }));
    });
    api.get("/securities/securities/").then((r) => setSecurities(r.data.results || r.data));
    api.get("/core/entities/").then((r) => setEntities(r.data.results || r.data));
    api.get(`/securities/deal-warrants/?deal=${initialDeal.id}`).then((r) => setWarrants(r.data.results || r.data || [])).catch(() => {});
  }, [initialDeal.id]);

  const set = (f) => (v) => setForm({ ...form, [f]: v });

  const save = () => {
    setSaving(true); setError("");
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) payload[k] = v;
    });
    api.patch(`/securities/deals/${deal.id}/`, payload)
      .then(() => onSaved())
      .catch((e) => { setSaving(false); setError(e.response?.data ? JSON.stringify(e.response.data) : "Failed"); });
  };

  const handleDelete = async () => {
    try {
      // Preview what will be deleted
      const preview = await api.get(`/securities/deals/${deal.id}/cascade/`);
      const s = preview.data.will_delete;
      const parts = [];
      if (s.deal_investments) parts.push(`\u2022 ${s.deal_investments} investor allocation(s)`);
      if (s.deal_investment_fees) parts.push(`\u2022 ${s.deal_investment_fees} fee record(s)`);
      if (s.tax_lots) parts.push(`\u2022 ${s.tax_lots} tax lot(s)`);
      if (s.ledger_entries) parts.push(`\u2022 ${s.ledger_entries} ledger entr(ies)`);
      if (s.fee_pool_recipients) parts.push(`\u2022 ${s.fee_pool_recipients} fee pool recipient(s)`);
      const detail = parts.length > 0 ? `\n\nThis will also delete:\n${parts.join("\n")}` : "";
      if (!window.confirm(`Delete deal "${s.deal_name}"?${detail}\n\nThis cannot be undone.`)) return;
      await api.delete(`/securities/deals/${deal.id}/cascade/`);
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message;
      alert("Delete failed: " + msg);
    }
  };

  const sec = deal.security_terms || {};
  const pct = (v) => v ? `${(Number(v) * 100).toFixed(2)}%` : "—";
  const money = (v) => v ? `$${Number(v).toLocaleString()}` : "—";

  // Taking a position to zero, from the screen rather than from a terminal.
  //
  // Brian, 17 September 2026, on High Wire Networks, Inc.: *"HWNI is closed.
  // We are going to take a zero on that one"*. The dry run runs first and its
  // figures are shown; nothing is written until the second click. Closing the
  // lots is the half that matters — everything that values this book reads
  // whether the lot is open, so a write-off that left them open would put the
  // position back on the dashboard tomorrow.
  const previewWriteOff = async () => {
    setWriteOff({ loading: true });
    try {
      const r = await api.post(`/securities/deals/${deal.id}/write-off/`, {});
      // Everything is ticked to begin with, because writing off the whole
      // deal is the ordinary case; a deal holding one dead line beside live
      // ones is the case the ticks exist for.
      setWriteOff({ preview: r.data, chosen: (r.data.lots || []).map((l) => l.lot_id) });
    } catch (err) {
      setWriteOff({ error: err.response?.data?.error || err.message });
    }
  };
  const commitWriteOff = async () => {
    const chosen = writeOff?.chosen || [];
    setWriteOff((w) => ({ ...w, loading: true }));
    try {
      const r = await api.post(`/securities/deals/${deal.id}/write-off/`,
        { confirm: true, lots: chosen });
      setWriteOff({ done: r.data });
      onSaved();
    } catch (err) {
      setWriteOff({ error: err.response?.data?.error || err.message });
    }
  };

  // A sale that found no shares, and the buy that was really its cover.
  //
  // Brian, 20 September 2026, on Artelo Biosciences and Nuvve Holding Corp
  // standing on the dashboard: *"at ITG, sold short and then bought back"* —
  // and then *"why would they not post? if blotter nets to zero then what else
  // did you need?"* They do post. Posting them is what leaves the long behind,
  // because the blotter poster has no short: the sale finds no lot and books
  // its whole proceeds as gain at zero cost, and the buy-back opens a lot of
  // its own. Nothing on an ITG row says short, so the pairing is a ruling
  // somebody makes — which is this, rather than a terminal.
  const findShorts = async () => {
    setShorts({ loading: true });
    try {
      const r = await api.get(`/securities/deals/${deal.id}/short-cover-candidates/`);
      const chosen = {};
      (r.data.pairs || []).forEach((pair) => {
        // Default to the candidate whose shares cancel the sale exactly; that
        // is the one worth looking at, and it is still only a proposal.
        const exact = pair.candidates.find((c) => Number(c.shares) === Number(pair.shares_sold));
        chosen[pair.sale.row] = (exact || pair.candidates[0]).row;
      });
      setShorts({ pairs: r.data.pairs || [], chosen });
    } catch (err) {
      setShorts({ error: err.response?.data?.error || err.message });
    }
  };
  const settleShort = async (pair, confirm) => {
    setShorts((x) => ({ ...x, loading: true }));
    try {
      const r = await api.post(`/securities/deals/${deal.id}/settle-short-cover/`, {
        sale: pair.sale.row,
        cover: shorts.chosen[pair.sale.row],
        ...(confirm ? { confirm: true } : {}),
      });
      if (confirm) {
        setShorts({ done: r.data });
        onSaved();
      } else {
        setShorts((x) => ({ ...x, loading: false, preview: r.data, previewFor: pair.sale.row }));
      }
    } catch (err) {
      setShorts({ error: err.response?.data?.error || err.message });
    }
  };

  return (
    <Modal title={`${deal.deal_name}`} onClose={onClose} width={660}>
      <ErrorBanner message={error} />

      {/* Deal header */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 16,
      }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Badge text={deal.status || "Active"} variant={deal.status === "Active" ? "green" : "gray"} />
          {deal.strategy_tag && (
            <Badge text={deal.strategy_tag} variant={
              deal.strategy_tag === "PIPE" ? "blue" : deal.strategy_tag === "ELOC" ? "green" : "purple"
            } />
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { window.location.href = `/deal-wizard?edit=${initialDeal.id}`; }} style={{
            padding: "6px 14px", borderRadius: 6,
            border: "1px solid #2563eb",
            background: "transparent",
            color: "#2563eb",
            fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}>
            Edit Deal
          </button>
          {!isEditing && (
            <button onClick={handleDelete} style={{
              padding: "6px 14px", borderRadius: 6,
              border: "1px solid #dc2626", background: "transparent",
              color: "#dc2626", fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}>
              Delete
            </button>
          )}
        </div>
      </div>

      {/* ── Write off to zero ──
          Shown only when the deal still holds something. The first click
          asks the server what it would do and prints the figures; the second
          writes them. Nothing is written by the first click. */}
      {!isEditing && (
        <div style={{
          marginBottom: 16, padding: "12px 14px", borderRadius: 6,
          border: `1px solid ${t.borderLight}`, background: t.bgSubtle,
        }}>
          {!writeOff && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 12, color: t.textSecondary }}>
                Positions on this deal are relieved to zero and closed — until then they
                stay in NAV and on the dashboard whatever the deal's status says. You pick
                which positions on the next step; to write off one tranche, use
                its Write off… under Tranches &amp; Add-ons.
              </span>
              <button onClick={previewWriteOff} style={{
                padding: "6px 14px", borderRadius: 6, whiteSpace: "nowrap",
                border: "1px solid #b45309", background: "transparent",
                color: "#b45309", fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}>Write off to zero…</button>
            </div>
          )}
          {writeOff?.loading && <div style={{ fontSize: 12, color: t.textSecondary }}>Working…</div>}
          {writeOff?.error && (
            <div style={{ fontSize: 12, color: "#dc2626" }}>
              {writeOff.error}{" "}
              <button onClick={() => setWriteOff(null)} style={{ border: 0, background: "none", textDecoration: "underline", cursor: "pointer", color: "inherit", font: "inherit" }}>Close</button>
            </div>
          )}
          {writeOff?.preview && !writeOff.loading && (
            <div style={{ fontSize: 12, color: t.text }}>
              {writeOff.preview.lots.length === 0 ? (
                <div>
                  {writeOff.preview.note}{" "}
                  <button onClick={() => setWriteOff(null)} style={{ border: 0, background: "none", textDecoration: "underline", cursor: "pointer", color: "inherit", font: "inherit" }}>Close</button>
                </div>
              ) : (
                <>
                  <strong>This will write off {writeOff.preview.lot_count} position
                    {writeOff.preview.lot_count === 1 ? "" : "s"} and close {writeOff.preview.lot_count === 1 ? "it" : "them"}.</strong>
                  <ul style={{ margin: "8px 0", paddingLeft: 4, color: t.textSecondary, listStyle: "none" }}>
                    {writeOff.preview.lots.map((lot) => {
                      const on = (writeOff.chosen || []).includes(lot.lot_id);
                      return (
                        <li key={lot.lot_id} style={{ marginBottom: 3 }}>
                          <label style={{ display: "flex", gap: 7, alignItems: "flex-start", cursor: "pointer" }}>
                            <input type="checkbox" checked={on} style={{ marginTop: 3 }}
                              onChange={() => setWriteOff((w) => ({
                                ...w,
                                chosen: on ? w.chosen.filter((id) => id !== lot.lot_id)
                                           : [...w.chosen, lot.lot_id],
                              }))} />
                            <span style={{ opacity: on ? 1 : 0.5 }}>
                              {lot.security} — {Number(lot.quantity).toLocaleString("en-US")} held,
                              carrying {Number(lot.carrying_value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {lot.currency}
                              {lot.posts_journal
                                ? ` → written off against ${lot.asset_account}`
                                : " → nothing to write off, closed only"}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                  <div style={{ marginBottom: 8 }}>
                    Total written off:{" "}
                    <strong>{writeOff.preview.lots
                      .filter((l) => (writeOff.chosen || []).includes(l.lot_id))
                      .reduce((sum, l) => sum + Number(l.carrying_value || 0), 0)
                      .toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                    {writeOff.preview.closed_without_journal > 0 &&
                      ` · ${writeOff.preview.closed_without_journal} already at nil cost, closed with no journal`}
                  </div>
                  {writeOff.preview.new_accounts?.length > 0 && (
                    <div style={{ marginBottom: 8, color: "#b45309" }}>
                      Opens {writeOff.preview.new_accounts.join(", ")} on the ledger for the first time.
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={commitWriteOff} disabled={!(writeOff.chosen || []).length} style={{
                      padding: "6px 14px", borderRadius: 6, border: "none",
                      background: "#b45309", color: "#fff", fontSize: 12,
                      fontWeight: 600, cursor: "pointer",
                      opacity: (writeOff.chosen || []).length ? 1 : 0.4,
                    }}>Write off {(writeOff.chosen || []).length} of {writeOff.preview.lots.length}</button>
                    <button onClick={() => setWriteOff(null)} style={{
                      padding: "6px 14px", borderRadius: 6,
                      border: `1px solid ${t.border}`, background: "transparent",
                      color: t.textSecondary, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}>Cancel</button>
                  </div>
                </>
              )}
            </div>
          )}
          {writeOff?.done && (
            <div style={{ fontSize: 12, color: t.text }}>
              Written off{" "}
              <strong>{Number(writeOff.done.total_written_off).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>{" "}
              across {writeOff.done.lot_count} position{writeOff.done.lot_count === 1 ? "" : "s"}, and closed
              {writeOff.done.lot_count === 1 ? " it" : " them"}. They are off the dashboard and out of NAV.
            </div>
          )}
        </div>
      )}

      {/* ── A short sold and bought back ──
          The blotter nets to zero and the book does not, because the sale was
          costed against nothing and the buy-back opened a position. One click
          asks what the pair looks like, the next shows what it would write,
          the third writes it. */}
      {!isEditing && (
        <div style={{
          marginBottom: 16, padding: "12px 14px", borderRadius: 6,
          border: `1px solid ${t.borderLight}`, background: t.bgSubtle,
        }}>
          {!shorts && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 12, color: t.textSecondary }}>
                A sale that found no shares books its whole proceeds as gain and leaves
                the buy-back standing as a position. If it was sold short and bought
                back, this nets the two.
              </span>
              <button onClick={findShorts} style={{
                padding: "6px 14px", borderRadius: 6, whiteSpace: "nowrap",
                border: `1px solid ${t.accent}`, background: "transparent",
                color: t.accent, fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}>Sold short and covered…</button>
            </div>
          )}
          {shorts?.loading && <div style={{ fontSize: 12, color: t.textSecondary }}>Working…</div>}
          {shorts?.error && (
            <div style={{ fontSize: 12, color: "#dc2626" }}>
              {shorts.error}{" "}
              <button onClick={() => setShorts(null)} style={{ border: 0, background: "none", textDecoration: "underline", cursor: "pointer", color: "inherit", font: "inherit" }}>Close</button>
            </div>
          )}
          {shorts?.pairs && !shorts.loading && shorts.pairs.length === 0 && (
            <div style={{ fontSize: 12, color: t.text }}>
              No zero-cost sale on this deal has an open position that could be its
              cover.{" "}
              <button onClick={() => setShorts(null)} style={{ border: 0, background: "none", textDecoration: "underline", cursor: "pointer", color: "inherit", font: "inherit" }}>Close</button>
            </div>
          )}
          {shorts?.pairs?.map((pair) => (
            <div key={pair.sale.row} style={{ fontSize: 12, color: t.text, marginBottom: 10 }}>
              <div style={{ marginBottom: 4 }}>
                <strong>{pair.sale.company || pair.sale.deal}</strong>{" "}
                {pair.sale.instrument} — sold {qty(pair.shares_sold)} shares on{" "}
                {pair.sale.date}, booked at nil cost with{" "}
                <strong>{num(pair.gain_booked_on_the_sale)}</strong> of gain.
              </div>
              <div style={{ marginBottom: 4, color: t.textSecondary }}>
                Covered by:{" "}
                <select
                  value={shorts.chosen[pair.sale.row] || ""}
                  onChange={(e) => setShorts((x) => ({
                    ...x, preview: null,
                    chosen: { ...x.chosen, [pair.sale.row]: Number(e.target.value) },
                  }))}
                  style={{ fontSize: 12, padding: "2px 6px" }}
                >
                  {pair.candidates.map((c) => (
                    <option key={c.row} value={c.row}>
                      {qty(c.shares)} shares bought {c.date} at {num(c.cost_basis)}
                    </option>
                  ))}
                </select>
                {!pair.netting && " — the shares do not cancel, so part of it stays open"}
              </div>
              {shorts.previewFor === pair.sale.row && shorts.preview ? (
                <div style={{ padding: "8px 10px", borderRadius: 4, background: t.bg, border: `1px solid ${t.borderLight}` }}>
                  Books {qty(shorts.preview.shares)} shares as a short sold on{" "}
                  {shorts.preview.sale.date} and covered on {shorts.preview.date}:
                  the {num(shorts.preview.reclassified.gain_removed)} of gain comes off,
                  and the realized result becomes{" "}
                  <strong>{num(shorts.preview.covered.realized_pnl)}</strong>.
                  {Number(shorts.preview.shares_left_short) > 0 &&
                    ` ${qty(shorts.preview.shares_left_short)} shares stay short.`}
                  {Number(shorts.preview.shares_left_long) > 0 &&
                    ` ${qty(shorts.preview.shares_left_long)} shares stay long.`}
                  <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                    <button onClick={() => settleShort(pair, true)} style={{
                      padding: "6px 14px", borderRadius: 6, border: 0,
                      background: t.accent, color: "#fff", fontSize: 12,
                      fontWeight: 600, cursor: "pointer",
                    }}>Book it</button>
                    <button onClick={() => setShorts((x) => ({ ...x, preview: null, previewFor: null }))} style={{
                      padding: "6px 14px", borderRadius: 6,
                      border: `1px solid ${t.borderLight}`, background: "transparent",
                      color: t.textSecondary, fontSize: 12, cursor: "pointer",
                    }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => settleShort(pair, false)} style={{
                  padding: "4px 12px", borderRadius: 6,
                  border: `1px solid ${t.accent}`, background: "transparent",
                  color: t.accent, fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}>Show me what this writes</button>
              )}
            </div>
          ))}
          {shorts?.done && (
            <div style={{ fontSize: 12, color: t.text }}>
              Booked as a short of {qty(shorts.done.shares)} shares, covered on{" "}
              {shorts.done.date}. Realized result{" "}
              <strong>{num(shorts.done.covered.realized_pnl)}</strong>, and the position
              is off the dashboard.
            </div>
          )}
        </div>
      )}

      {isEditing ? (
        /* ── Edit mode ── */
        <>
          <FormRow>
            <FormField label="Deal Name" required>
              <Input value={form.deal_name} onChange={set("deal_name")} />
            </FormField>
            <FormField label="Status">
              <Select value={form.status} onChange={set("status")} options={[
                { value: "Active", label: "Active" },
                { value: "Closed", label: "Closed" },
                { value: "Pending", label: "Pending" },
              ]} />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Security">
              <Select value={form.security} onChange={(v) => {
                const s = securities.find((s) => String(s.id) === String(v));
                setForm({ ...form, security: v, counterparty: s?.issuer || form.counterparty });
              }} options={securities.map((s) => ({ value: s.id, label: `${s.ticker} — ${s.name}` }))} />
            </FormField>
            <FormField label="Entity">
              <Select value={form.entity} onChange={set("entity")}
                options={entities.map((e) => ({ value: e.id, label: e.name }))} />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Strategy">
              <Select value={form.strategy_tag} onChange={set("strategy_tag")} options={[
                { value: "Convertible Note", label: "Convertible Note" },
                { value: "Convertible Preferred", label: "Convertible Preferred" },
                { value: "ELOC", label: "ELOC" },
                { value: "ACE Deal", label: "ACE Deal" },
                { value: "Registered Direct", label: "Registered Direct (RD)" },
                { value: "PPLN", label: "PPLN" },
                { value: "IPO", label: "IPO" },
                { value: "deSPAC - Convertible Note", label: "deSPAC - Convertible Note" },
                { value: "Common Equity PIPE", label: "Common Equity PIPE" },
                { value: "Bridge Loan", label: "Bridge Loan" },
                { value: "Warrant", label: "Warrant" },
                { value: "Other", label: "Other" },
              ]} />
            </FormField>
            <FormField label="Funding Date">
              <Input type="date" value={form.funding_date} onChange={set("funding_date")} />
            </FormField>
          </FormRow>

          <SectionLabel label="Investment Economics" />
          <FormRow>
            <FormField label="Face Value (Note Principal)">
              <Input type="number" value={form.face_value} onChange={(v) => {
                const fv = parseFloat(v) || 0;
                const na = parseFloat(form.net_amount) || 0;
                const oidAmt = na > 0 ? (fv - na).toFixed(4) : form.oid_amount;
                const oidPct = na > 0 && fv > 0 ? ((fv - na) / fv).toFixed(6) : form.oid_pct;
                setForm({ ...form, face_value: v, oid_amount: oidAmt, oid_pct: oidPct });
              }} step="0.01" />
            </FormField>
            <FormField label="Net Amount (Cash Invested)">
              <Input type="number" value={form.net_amount} onChange={(v) => {
                const na = parseFloat(v) || 0;
                const fv = parseFloat(form.face_value) || 0;
                const oidAmt = fv > 0 ? (fv - na).toFixed(4) : form.oid_amount;
                const oidPct = fv > 0 ? ((fv - na) / fv).toFixed(6) : form.oid_pct;
                setForm({ ...form, net_amount: v, oid_amount: oidAmt, oid_pct: oidPct });
              }} step="0.01" />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="OID Amount">
              <Input type="number" value={form.oid_amount} onChange={set("oid_amount")} step="0.01" />
            </FormField>
            <FormField label="OID %">
              <Input type="pct" value={form.oid_pct} onChange={set("oid_pct")} />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Total Investment Size">
              <Input type="number" value={form.deal_size} onChange={set("deal_size")} step="0.01" />
            </FormField>
            <FormField label="Warrant Coverage %">
              <Input type="pct" value={form.warrant_coverage_pct} onChange={set("warrant_coverage_pct")} step="0.001" />
            </FormField>
          </FormRow>
          <FormField label="Coupon Frequency">
            <Select value={form.coupon_frequency} onChange={set("coupon_frequency")} options={[
              { value: "", label: "— Select —" },
              { value: "Monthly", label: "Monthly" },
              { value: "Quarterly", label: "Quarterly" },
              { value: "Semi-Annual", label: "Semi-Annual" },
              { value: "Annual", label: "Annual" },
              { value: "At Maturity", label: "At Maturity" },
              { value: "PIK", label: "PIK (accrues)" },
            ]} />
          </FormField>

          <SectionLabel label="Ownership & Classification" />
          <FormRow>
            <FormField label="Vintage Year">
              <Input type="number" value={form.vintage_year} onChange={set("vintage_year")} placeholder="2025" />
            </FormField>
            <FormField label="Ownership %">
              <Input type="pct" value={form.beneficial_ownership_pct} onChange={set("beneficial_ownership_pct")} />
            </FormField>
          </FormRow>
          <FormField label="Affiliate Status">
            <Select value={form.affiliate_status} onChange={set("affiliate_status")} options={[
              { value: "Non-Affiliate", label: "Non-Affiliate" },
              { value: "Affiliate", label: "Affiliate" },
              { value: "10% Holder", label: "10%+ Holder" },
            ]} />
          </FormField>

          <SectionLabel label="Fee Structure" />
          <FormRow>
            <FormField label="Management Fee %">
              <Input type="pct" value={form.management_fee_pct} onChange={set("management_fee_pct")} placeholder="2" />
            </FormField>
            <FormField label="Incentive Fee %">
              <Input type="pct" value={form.incentive_fee_pct} onChange={set("incentive_fee_pct")} placeholder="20" />
            </FormField>
          </FormRow>
          <FormField label="Preferred Return %">
            <Input type="pct" value={form.preferred_return_pct} onChange={set("preferred_return_pct")} placeholder="8" />
          </FormField>

          <FormField label="Notes">
            <TextArea value={form.notes} onChange={set("notes")} />
          </FormField>
          <SubmitButton onClick={save} loading={saving} label="Save Changes" />
        </>
      ) : (
        /* ── View mode ── */
        <>
          {/* Deal info */}
          <DetailSection label="Deal Information">
            <DetailRow label="Counterparty / Issuer" value={deal.counterparty} />
            <DetailRow label="Security" value={`${deal.security_ticker || ""}`} />
            <DetailRow label="Entity" value={deal.entity_name} />
            <DetailRow label="Strategy" value={deal.strategy_tag} />
            <DetailRow label="Funding Date" value={deal.funding_date || "—"} />
            <DetailRow label="Vintage" value={deal.vintage_year} />
            <DetailRow label="Affiliate Status" value={deal.affiliate_status} />
            {deal.beneficial_ownership_pct && (
              <DetailRow label="Ownership %" value={pct(deal.beneficial_ownership_pct)} />
            )}
          </DetailSection>

          {/* Investment economics */}
          <DetailSection label="Investment Economics">
            <DetailRow label="Face Value" value={money(deal.face_value)} />
            <DetailRow label="Net Amount (Cash)" value={money(deal.net_amount)} />
            <DetailRow label="OID Amount" value={money(deal.oid_amount)} />
            <DetailRow label="OID %" value={deal.oid_pct ? pct(deal.oid_pct) : "—"} />
            <DetailRow label="Investment Size" value={money(deal.deal_size)} />
            <DetailRow label="Warrant Coverage" value={deal.warrant_coverage_pct ? pct(deal.warrant_coverage_pct) : "—"} />
            <DetailRow label="Coupon Frequency" value={deal.coupon_frequency || "—"} />
          </DetailSection>

          {/* Security terms from linked security */}
          {sec && sec.ticker && (
            <DetailSection label={`Security Terms — ${sec.ticker} (${sec.instrument_type})`}>
              {sec.instrument_type === "Note" && (
                <>
                  <DetailRow label="Principal" value={money(sec.principal_amount)} />
                  <DetailRow label="Coupon Rate" value={sec.coupon_rate ? pct(sec.coupon_rate) : "—"} />
                  <DetailRow label="Day Count" value={sec.day_count || "—"} />
                  <DetailRow label="Interest Mode" value={sec.interest_mode || "—"} />
                  <DetailRow label="Issue Date" value={sec.issue_date || "—"} />
                  <DetailRow label="Maturity Date" value={sec.maturity_date || "—"} />
                  <DetailRow label="Fixed Conversion Price" value={sec.conversion_price ? `$${Number(sec.conversion_price).toFixed(4)}` : "—"} />
                  <DetailRow label="Variable Discount" value={sec.conversion_discount_pct ? pct(sec.conversion_discount_pct) : "—"} />
                  {sec.conversion_vwap_lookback && (
                    <DetailRow label="VWAP Lookback" value={`${sec.conversion_vwap_lookback} days`} />
                  )}
                  <DetailRow label="Floor Price" value={sec.conversion_floor_price ? `$${Number(sec.conversion_floor_price).toFixed(4)}` : "—"} />
                  {sec.conversion_ceiling_price && (
                    <DetailRow label="Ceiling Price" value={`$${Number(sec.conversion_ceiling_price).toFixed(4)}`} />
                  )}
                </>
              )}
              {sec.instrument_type === "Warrant" && (
                <>
                  <DetailRow label="Strike Price" value={sec.strike_price ? `$${Number(sec.strike_price).toFixed(4)}` : "—"} />
                  <DetailRow label="Warrant Shares" value={sec.warrant_shares ? Number(sec.warrant_shares).toLocaleString() : "—"} />
                  <DetailRow label="Expiry" value={sec.warrant_expiry || "—"} />
                </>
              )}
              {sec.instrument_type === "ELOC" && (
                <>
                  <DetailRow label="Facility Size" value={money(sec.eloc_commitment_amount)} />
                  <DetailRow label="Discount Tier 1" value={sec.eloc_discount_pct ? pct(sec.eloc_discount_pct) : "—"} />
                  {sec.eloc_discount_pct_2 && (
                    <DetailRow label="Discount Tier 2" value={pct(sec.eloc_discount_pct_2)} />
                  )}
                  {sec.eloc_discount_pct_3 && (
                    <DetailRow label="Discount Tier 3" value={pct(sec.eloc_discount_pct_3)} />
                  )}
                  <DetailRow label="Pricing Period" value={sec.eloc_pricing_days ? `${sec.eloc_pricing_days} days` : "—"} />
                  <DetailRow label="Settlement Days" value={sec.eloc_settlement_days ? `T+${sec.eloc_settlement_days}` : "—"} />
                  {sec.eloc_max_advance && <DetailRow label="Max Advance per Put" value={money(sec.eloc_max_advance)} />}
                  {sec.eloc_ownership_limit_pct && <DetailRow label="Ownership Limit" value={pct(sec.eloc_ownership_limit_pct)} />}
                  {sec.eloc_exchange_cap_pct && <DetailRow label="Exchange Cap" value={pct(sec.eloc_exchange_cap_pct)} />}
                  {sec.eloc_commitment_period_months && <DetailRow label="Commitment Period" value={`${sec.eloc_commitment_period_months} months`} />}
                  {(sec.eloc_commitment_fee_shares || sec.eloc_commitment_fee_amount) && (
                    <DetailRow label="Commitment Fee" value={
                      [
                        sec.eloc_commitment_fee_shares ? `${Number(sec.eloc_commitment_fee_shares).toLocaleString()} shares` : null,
                        sec.eloc_commitment_fee_amount ? `$${Number(sec.eloc_commitment_fee_amount).toLocaleString()}` : null,
                      ].filter(Boolean).join(" + ")
                    } />
                  )}
                  <DetailRow label="Exchange" value={sec.eloc_exchange || "—"} />
                </>
              )}
              {sec.instrument_type === "Stock" && (
                <>
                  <DetailRow label="Last Price" value={sec.last_price ? `$${Number(sec.last_price).toFixed(4)}` : "—"} />
                  {sec.shares_outstanding && (
                    <DetailRow label="Shares Outstanding" value={Number(sec.shares_outstanding).toLocaleString()} />
                  )}
                </>
              )}
            </DetailSection>
          )}

          {/* Fee structure */}
          {(deal.management_fee_pct || deal.incentive_fee_pct || deal.preferred_return_pct) && (
            <DetailSection label="Fee Structure">
              {deal.management_fee_pct && <DetailRow label="Management Fee" value={pct(deal.management_fee_pct)} />}
              {deal.incentive_fee_pct && <DetailRow label="Incentive Fee" value={pct(deal.incentive_fee_pct)} />}
              {deal.preferred_return_pct && <DetailRow label="Preferred Return" value={pct(deal.preferred_return_pct)} />}
            </DetailSection>
          )}

          {/* Warrants */}
          <DetailSection label={`Warrants (${warrants.length})`}>
            {warrants.length === 0 ? (
              <div style={{ color: "#94a3b8", fontSize: 12 }}>No warrants attached to this deal</div>
            ) : warrants.map((w, i) => (
              <div key={w.id} style={{ marginBottom: i < warrants.length - 1 ? 12 : 0, paddingBottom: i < warrants.length - 1 ? 12 : 0, borderBottom: i < warrants.length - 1 ? "1px solid #e2e8f0" : "none" }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{w.series_name || `Warrant ${i + 1}`}</div>
                <DetailRow label="Strike Price" value={w.strike_price ? `$${Number(w.strike_price).toFixed(4)}` : w.strike_price_method || "TBD"} />
                <DetailRow label="Shares" value={w.num_shares ? Number(w.num_shares).toLocaleString() : "TBD"} />
                <DetailRow label="Expiry" value={w.expiry_date || w.expiry_method || "—"} />
                {w.is_prefunded && <DetailRow label="Pre-Funded" value={`Yes — $${w.prefunded_exercise_price || "0.0001"}/share`} />}
                {w.milestone_trigger && <DetailRow label="Milestone" value={w.milestone_trigger} />}
                {w.price_reset_enabled && <DetailRow label="Price Reset" value={`Floor: ${w.price_reset_floor_pct ? (Number(w.price_reset_floor_pct) * 100).toFixed(0) + "%" : "—"} of original`} />}
                {w.exercise_conditions && <DetailRow label="Conditions" value={w.exercise_conditions} />}
                {w.notes && <DetailRow label="Notes" value={w.notes} />}
                <button onClick={async () => { if (window.confirm(`Delete ${w.series_name || "this warrant"}?`)) { await api.delete(`/securities/deal-warrants/${w.id}/`); setWarrants(warrants.filter(x => x.id !== w.id)); }}} style={{ marginTop: 4, padding: "3px 10px", borderRadius: 4, border: "1px solid #dc2626", background: "transparent", color: "#dc2626", fontSize: 11, cursor: "pointer" }}>Remove</button>
              </div>
            ))}
            {!isEditing && (
              <button onClick={() => setShowAddWarrant(!showAddWarrant)} style={{ marginTop: 8, padding: "6px 14px", borderRadius: 6, border: "1px solid #2563eb", background: "transparent", color: "#2563eb", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                {showAddWarrant ? "Cancel" : "+ Add Warrant"}
              </button>
            )}
            {showAddWarrant && (
              <div style={{ marginTop: 12, padding: 12, background: "#f0f9ff", borderRadius: 8, border: "1px solid #bae6fd" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <FormField label="Series Name"><Input value={newWarrant.series_name} onChange={(v) => setNewWarrant({...newWarrant, series_name: v})} placeholder="e.g., Series B Warrants" /></FormField>
                  <FormField label="Strike Price"><Input type="number" value={newWarrant.strike_price} onChange={(v) => setNewWarrant({...newWarrant, strike_price: v})} placeholder="$0.00" /></FormField>
                  <FormField label="Number of Shares"><Input type="number" value={newWarrant.num_shares} onChange={(v) => setNewWarrant({...newWarrant, num_shares: v})} /></FormField>
                  <FormField label="Expiry Date"><Input type="date" value={newWarrant.expiry_date} onChange={(v) => setNewWarrant({...newWarrant, expiry_date: v})} /></FormField>
                  <FormField label="Strike Price Method"><Input value={newWarrant.strike_price_method} onChange={(v) => setNewWarrant({...newWarrant, strike_price_method: v})} placeholder="e.g., 90% of closing price" /></FormField>
                  <FormField label="Expiry Method"><Input value={newWarrant.expiry_method} onChange={(v) => setNewWarrant({...newWarrant, expiry_method: v})} placeholder="e.g., 18 months after approval" /></FormField>
                </div>
                <FormField label="Exercise Conditions"><TextArea value={newWarrant.exercise_conditions} onChange={(v) => setNewWarrant({...newWarrant, exercise_conditions: v})} placeholder="e.g., Requires stockholder approval" rows={2} /></FormField>
                <FormField label="Milestone Trigger"><Input value={newWarrant.milestone_trigger} onChange={(v) => setNewWarrant({...newWarrant, milestone_trigger: v})} placeholder="e.g., Announcement of filaggrin study data" /></FormField>
                <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                  <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                    <input type="checkbox" checked={newWarrant.is_prefunded} onChange={(e) => setNewWarrant({...newWarrant, is_prefunded: e.target.checked})} /> Pre-Funded
                  </label>
                  <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                    <input type="checkbox" checked={newWarrant.price_reset_enabled} onChange={(e) => setNewWarrant({...newWarrant, price_reset_enabled: e.target.checked})} /> Price Reset
                  </label>
                </div>
                {newWarrant.price_reset_enabled && (
                  <FormField label="Reset Floor (% of original)"><Input type="pct" value={newWarrant.price_reset_floor_pct} onChange={(v) => setNewWarrant({...newWarrant, price_reset_floor_pct: v})} placeholder="50" /></FormField>
                )}
                <FormField label="Notes"><TextArea value={newWarrant.notes} onChange={(v) => setNewWarrant({...newWarrant, notes: v})} rows={2} /></FormField>
                <button onClick={async () => {
                  try {
                    const payload = { deal: deal.id };
                    Object.entries(newWarrant).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) payload[k] = v; });
                    const res = await api.post("/securities/deal-warrants/", payload);
                    setWarrants([...warrants, res.data]);
                    setShowAddWarrant(false);
                    setNewWarrant({ series_name: "", strike_price: "", num_shares: "", expiry_date: "", strike_price_method: "", expiry_method: "", is_prefunded: false, exercise_conditions: "", milestone_trigger: "", price_reset_enabled: false, price_reset_floor_pct: "", notes: "" });
                  } catch (err) { alert("Failed: " + (err.response?.data ? JSON.stringify(err.response.data) : err.message)); }
                }} style={{ marginTop: 8, padding: "8px 20px", borderRadius: 6, border: "none", background: "#2563eb", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Save Warrant</button>
              </div>
            )}
          </DetailSection>

          {/* Tranches / Add-ons */}
          <DetailSection label="Tranches & Add-ons">
            <DealTranchesPanel dealId={deal.id} />
          </DetailSection>

          {/* Capital Sources */}
          <DetailSection label="Capital Sources">
            {deal && <DealSecuritiesPanel dealId={deal.id} />}
              <CapitalSourcesPanel dealId={deal.id} />
          </DetailSection>

          {deal.notes && (
            <DetailSection label="Notes">
              <div style={{ fontSize: 12, color: "#475569", whiteSpace: "pre-wrap" }}>{deal.notes}</div>
            </DetailSection>
          )}
        </>
      )}
    </Modal>
  );
}


// ═══════════════════════════════════════════════════════════════
// SHARED UI COMPONENTS
// ═══════════════════════════════════════════════════════════════

function DetailSection({ label, children }) {
  return (
    <div style={{
      background: "#f8fafc", borderRadius: 8, padding: 14,
      marginBottom: 12, border: "1px solid #e2e8f0",
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: "#64748b",
        textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8,
      }}>{label}</div>
      <div style={{ fontSize: 12, lineHeight: 1.8 }}>{children}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span style={{ color: "#64748b" }}>{label}</span>
      <span style={{ fontWeight: 500, color: "#1e293b" }}>{value || "—"}</span>
    </div>
  );
}

function SectionLabel({ label, color, size }) {
  return (
    <div style={{
      fontSize: size === "sm" ? 11 : 13,
      fontWeight: 700,
      color: color || "#1e293b",
      margin: size === "sm" ? "10px 0 6px" : "18px 0 10px",
      padding: size === "sm" ? 0 : "8px 0 4px",
      borderTop: size === "sm" ? "none" : "1px solid #e5e7eb",
    }}>
      {label}
    </div>
  );
}
